import { RwGlyph, RwBadge, RwLevelPill } from '@components/atoms';
import { RwLoading } from '@components/RwLoading';
import { t } from '@services/i18n';
import { useApiQuery } from '@hooks/useApiQuery';
import { RwEmpty } from '@components/RwEmpty';
import { MyLedger } from './MyLedger';
import type { MemberScreenProps } from './types';
import type { MemberScreenId } from '../navigation';
import type { Me, NextAction, LevelEconomy, Badge, MyLedgerEntry } from '@shared/types';

/**
 * Member dashboard — answers "what's my status? what have I earned? what can I
 * do next?". Hero with rank/points/reputation/streak, a level track derived
 * from lifetime Reputation, transparent next-action suggestions, recent
 * activity (the personal ledger, sliced) and the earned-badges row. Tab-jump
 * CTAs route through `setScreen`. Renders only real data from the `dashboard`
 * endpoint; shows zeroed/empty states when there is none.
 */

/** Map a next-action CTA label to the member tab it should jump to. */
function ctaTarget(cta: string): MemberScreenId {
  return cta === 'View badge' ? 'u-badges' : 'u-howto';
}

interface DashboardResponse {
  me: Me;
  next: NextAction[];
  levels: LevelEconomy[];
  badges: Badge[];
  ledger: MyLedgerEntry[];
}

const emptyMe: Me = {
  id: 0,
  name: '',
  handle: '',
  avatar: '',
  points: 0,
  rep: 0,
  rank: 0,
  totalUsers: 0,
  joined: '—',
  level: '',
  nextLevel: null,
  streak: 0,
  longestStreak: 0,
  pointsExpiring: 0,
  expiringOn: '—',
  nextBadge: { name: '', progress: 0, remaining: 0 },
};

export function UserDashboard({ setScreen }: MemberScreenProps) {
  const { data, isLoading } = useApiQuery<DashboardResponse>(['rw-dashboard'], 'dashboard', {
    fallback: { me: emptyMe, next: [], levels: [], badges: [], ledger: [] },
  });

  if (isLoading) return <RwLoading variant="dashboard" />;

  const me = data?.me ?? emptyMe;
  const next = data?.next ?? [];
  const levels = data?.levels ?? [];
  const badges = data?.badges ?? [];
  const ledger = data?.ledger ?? [];

  const earned = badges.filter((b) => b.earned);
  const curLevelIdx = levels.findIndex((l) => l.name === me.level);
  const nextLevel = curLevelIdx < levels.length - 1 ? levels[curLevelIdx + 1] : null;

  return (
    <div className="rw-page rw-user">
      <header className="rw-hero">
        <div className="rw-hero-l">
          <div className="rw-eyebrow">{t('COM_REWARDIFY_UDASH_WELCOME', 'Welcome back')}</div>
          <h1 className="rw-hero-name">{me.name.split(' ')[0]}.</h1>
          <p className="rw-hero-sub">
            {t('COM_REWARDIFY_UDASH_YOURE', "You're")}{' '}
            <strong>{me.rank === 1 ? '1st' : `#${me.rank}`}</strong>{' '}
            {t('COM_REWARDIFY_UDASH_OF', 'of')}{' '}
            <strong>{me.totalUsers.toLocaleString()}</strong>{' '}
            {t('COM_REWARDIFY_UDASH_MEMBERS', 'members.')}
          </p>
          <div className="rw-hero-meta">
            <div>
              <span className="rw-fg2">{t('COM_REWARDIFY_UDASH_MEMBER_SINCE', 'Member since')}</span>
              <strong>{me.joined}</strong>
            </div>
            <div>
              <span className="rw-fg2">{t('COM_REWARDIFY_UDASH_LEVEL', 'Level')}</span>
              <strong>
                <RwLevelPill level={me.level} />
              </strong>
            </div>
            <div>
              <span className="rw-fg2">{t('COM_REWARDIFY_UDASH_LOGIN_STREAK', 'Login streak')}</span>
              <strong>{t('COM_REWARDIFY_UDASH_DAYS', '%d days', me.streak)}</strong>
            </div>
          </div>
        </div>
        <div className="rw-hero-r" style={{ minWidth: 380 }}>
          <div className="rw-wallet rw-wallet--inline">
            <div className="rw-wallet-card">
              <div className="rw-wallet-l">{t('COM_REWARDIFY_UDASH_COMMUNITY_POINTS', 'Community Points')}</div>
              <div className="rw-wallet-v" style={{ color: 'var(--rw-accent-fg)' }}>
                {me.points.toLocaleString()}
              </div>
              <div className="rw-wallet-s">
                {t('COM_REWARDIFY_UDASH_EXPIRE', '%s expire %s', me.pointsExpiring, me.expiringOn)}
              </div>
            </div>
            <div className="rw-wallet-card">
              <div className="rw-wallet-l">{t('COM_REWARDIFY_UDASH_REPUTATION', 'Reputation')}</div>
              <div className="rw-wallet-v">{me.rep.toLocaleString()}</div>
              <div className="rw-wallet-s">{t('COM_REWARDIFY_UDASH_LIFETIME_NEVER', 'lifetime · never expires')}</div>
            </div>
          </div>
        </div>
      </header>

      <section className="rw-card">
        <div className="rw-card-h">
          <div>
            <h3 className="rw-card-t">{t('COM_REWARDIFY_UDASH_YOUR_LEVEL', 'Your level')}</h3>
            <p className="rw-card-s">
              {t('COM_REWARDIFY_UDASH_DERIVED_FROM_REP', 'Derived from lifetime Reputation.')}{' '}
              {nextLevel
                ? t(
                    'COM_REWARDIFY_UDASH_REP_TO_NEXT',
                    '%s REP to %s.',
                    (nextLevel.min - me.rep).toLocaleString(),
                    nextLevel.name,
                  )
                : t('COM_REWARDIFY_UDASH_TOP_LEVEL', 'Top level reached.')}
            </p>
          </div>
          <RwLevelPill level={me.level} />
        </div>
        <div className="rw-leveltrack">
          {levels.map((l, i) => (
            <div key={l.id} className={`rw-leveltrack-seg ${i <= curLevelIdx ? 'on' : ''}`}>
              {i === curLevelIdx && <span className="rw-leveltrack-pin" />}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }} className="rw-fg2 rw-mono">
          {levels.map((l) => (
            <span key={l.id}>{l.name}</span>
          ))}
        </div>
      </section>

      <div>
        <h3 className="rw-section-t">{t('COM_REWARDIFY_UDASH_WHAT_NEXT', 'What you can do next')}</h3>
        <div className="rw-next">
          {next.map((n, i) => (
            <div key={i} className="rw-next-card">
              <div className="rw-next-icon">
                <RwGlyph kind={n.icon} size={18} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: 13 }}>{n.headline}</div>
                <div className="rw-fg2" style={{ fontSize: 12, marginTop: 2 }}>
                  {n.sub}
                </div>
              </div>
              <button
                type="button"
                className="rw-btn rw-btn-ghost rw-btn-sm"
                onClick={() => setScreen(ctaTarget(n.cta))}
              >
                {n.cta}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="rw-grid-1-1">
        <section className="rw-card">
          <div className="rw-card-h">
            <h3 className="rw-card-t">{t('COM_REWARDIFY_UDASH_RECENT_ACTIVITY', 'Recent activity')}</h3>
            <button
              type="button"
              className="rw-btn rw-btn-ghost rw-btn-sm"
              onClick={() => setScreen('u-activity')}
            >
              {t('COM_REWARDIFY_UDASH_ALL_REWARDS', 'All rewards')} →
            </button>
          </div>
          <MyLedger rows={ledger.slice(0, 5)} />
        </section>
        <section className="rw-card">
          <div className="rw-card-h">
            <h3 className="rw-card-t">{t('COM_REWARDIFY_UDASH_BADGES_EARNED', 'Badges earned (%d)', earned.length)}</h3>
            <button
              type="button"
              className="rw-btn rw-btn-ghost rw-btn-sm"
              onClick={() => setScreen('u-badges')}
            >
              {t('COM_REWARDIFY_UDASH_ALL', 'All')} →
            </button>
          </div>
          {earned.length === 0 ? (
            <RwEmpty
              title={t('COM_REWARDIFY_UDASH_NO_BADGES_TITLE', 'No badges earned yet')}
              hint={t('COM_REWARDIFY_UDASH_NO_BADGES_HINT', 'Earn badges by taking part in the community.')}
            />
          ) : (
            <div className="rw-badge-row">
              {earned.map((b) => (
                <div key={b.id} className="rw-badge-row-item">
                  <RwBadge tier={b.tier} icon={b.icon} size={48} />
                  <div style={{ fontSize: 11, fontWeight: 500, marginTop: 6, textAlign: 'center' }}>{b.name}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
